import { unwrapModifiedEventLog } from '@lp/features/RoutePlanner/components/LogisticsChangeLog/utils/helpers';
import { ModifiedLogValue } from '@lp/features/RoutePlanner/components/LogisticsChangeLog/utils/types';
import {
  AirportLogLocation,
  BaseModifiedGroundLogLocation,
  CustomAddressLogLocation,
  FacilityLogLocation,
  GroundProviderExceptionKeys,
  HomeAddressLogLocation,
  LegLocationTypeEnum,
  LogisticsChangeLogType,
  LogisticsResourceType,
  ModifiedGroundLogAirportLocation,
  ModifiedGroundLogFacilityLocation,
  ModifiedGroundLogLocation,
  ModifiedLogisticsChangeLog,
} from '@/api/apis/route';
import { CaseResourceType } from '@/api/apis/case';
import { getFullName } from '@/utils/helpers';

export const convertGroundLogLocation = (
  location: ModifiedGroundLogLocation,
): AirportLogLocation | FacilityLogLocation | HomeAddressLogLocation | CustomAddressLogLocation | null => {
  switch (location.locationType) {
    case LegLocationTypeEnum.airport:
      const airport = location as ModifiedGroundLogAirportLocation;

      return {
        id: airport.id,
        icao: airport.details.icao,
        iata: airport.details.iata,
        locationType: airport.locationType,
        fboLocation: airport.details.fboLocation,
      } as AirportLogLocation;
    case LegLocationTypeEnum.hub:
    case LegLocationTypeEnum.hospital:
    case LegLocationTypeEnum.txcenter:
      const facility = location as ModifiedGroundLogFacilityLocation;

      return {
        id: facility.id,
        name: facility.details.name,
        code: facility.details.code,
        state: facility.location.state,
        city: facility.location.city,
        country: facility.location.country,
        locationType: location.locationType,
      } as FacilityLogLocation;
    case LegLocationTypeEnum.custom:
    case LegLocationTypeEnum.homeResidence:
    default:
      const custom = location as BaseModifiedGroundLogLocation;

      if (!custom) return null;

      return {
        id: custom.id,
        location: custom.location,
        locationType: custom.locationType,
      } as HomeAddressLogLocation | CustomAddressLogLocation;
  }
};

export const getCurrentLocationValues = (
  log: ModifiedLogisticsChangeLog,
  locationKey: 'departureLocation' | 'arrivalLocation',
): AirportLogLocation | FacilityLogLocation | HomeAddressLogLocation | CustomAddressLogLocation | null => {
  switch (log.$type) {
    case LogisticsChangeLogType.TmdxJetAviaLegModifiedLog:
    case LogisticsChangeLogType.SupplementalLiftAviaLegModifiedLog:
    case LogisticsChangeLogType.TxCenterProviderAviaLegModifiedLog:
    case LogisticsChangeLogType.CommercialFlightAviaLegModifiedLog:
    case LogisticsChangeLogType.SabreCommercialAviaLegModifiedLog:
      return unwrapModifiedEventLog(log.leg[locationKey]);
    case LogisticsChangeLogType.TmdxAffiliateGroundLegModifiedLog:
    case LogisticsChangeLogType.TxCenterProviderGroundLegModifiedLog:
    case LogisticsChangeLogType.CurriServiceGroundLegModifiedLog:
    default:
      return convertGroundLogLocation(
        unwrapModifiedEventLog<ModifiedGroundLogLocation, GroundProviderExceptionKeys>(log.leg[locationKey]),
      );
  }
};

export const getResourceModifiedLogValue = (resource: LogisticsResourceType): ModifiedLogValue | null => {
  if (!resource.resourceId) return null;

  switch (resource.resourceType) {
    case CaseResourceType.OCSConsole:
      return {
        type: 'device',
        title: resource.fullSerialNumber,
        imageUrl: resource.logoPath,
      };
    case CaseResourceType.ClinicalSpecialist:
      return {
        type: 'user',
        title: `${getFullName(resource.firstName, resource.lastName)} (CS)`,
        imageUrl: resource.photoPath,
      };
    case CaseResourceType.Crew:
      return {
        type: 'user',
        title: `${getFullName(resource.firstName, resource.lastName)} (TMDX Pilot)`,
      };
    case CaseResourceType.Surgeon:
      return {
        type: 'user',
        title: `${getFullName(resource.firstName, resource.lastName)} (RS)`,
      };
    case CaseResourceType.Aircraft:
      return {
        type: 'text',
        title: resource.tailNumber,
      };
    default:
      return null;
  }
};
